import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import type { AgentType, AssistantConfig } from '@ardoq/api-types';
import { IconButton } from '@ardoq/button';
import { currentTimestamp } from '@ardoq/date-time';
import { colors, s8, transitionTokens } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import { IconName } from '@ardoq/icons';
import { dispatchAction } from '@ardoq/rxbeach';
import { scrollToBottom } from '../../actions';
import type { AssistantBanner, HistoryPlacement } from '../../assistantTypes';
import {
  type AssistantDocumentsProps,
  FileDropZone,
} from '../../documents/components';
import type { AssistantCommands } from '../../types';
import { Toolbox } from '../Toolbox';
import { ChatFooter } from './ChatFooter';

export type AssistantChatFooterProps = {
  commands: AssistantCommands;
  i18nLocale: I18nLocale;
  historyPlacement: HistoryPlacement;
  input: string;
  placeholder: string;
  chatSessionId: string | null;
  isRequestPending: boolean;
  isInputBlocked: boolean;
  isEditingPlan: boolean;
  isCancelRequestPending: boolean;
  canSendMessage: boolean;
  agentType: AgentType | undefined;
  assistantConfigInternal: AssistantConfig | null;
  documents: AssistantDocumentsProps;
  pageName: string;
  pageIcon: IconName | undefined;
  dataScopeTooltip: string;
  hasMessages: boolean;
  showScrollDownButton: boolean;
  hasPendingInputFocusRequest: boolean;
  banners: AssistantBanner[];
};

export const AssistantChatFooter = ({
  commands,
  i18nLocale,
  historyPlacement,
  input,
  placeholder,
  chatSessionId,
  isRequestPending,
  isInputBlocked,
  isEditingPlan,
  isCancelRequestPending,
  canSendMessage,
  agentType,
  assistantConfigInternal,
  documents,
  pageName,
  pageIcon,
  dataScopeTooltip,
  hasMessages,
  showScrollDownButton,
  hasPendingInputFocusRequest,
  banners,
}: AssistantChatFooterProps) => {
  const isScrollToBottomVisible = hasMessages && showScrollDownButton;

  const sendMessage = () => {
    if (!canSendMessage || isRequestPending) return;

    commands.sendWithHistory({
      content: input,
      chatSessionId,
      timestamp: currentTimestamp(),
      clientRequestId: uuidv4(),
      webSearchEnabled: documents.webSearchEnabled,
      source: 'chatInput',
      agentType,
      documents: documents.selectedDocuments.map(
        ({ id, name, type, resourceType }) => ({
          id,
          name,
          type,
          resourceType,
        })
      ),
    });
  };

  return (
    <FooterAnchor>
      <ScrollToBottomButtonContainer
        $isVisible={isScrollToBottomVisible}
        inert={!isScrollToBottomVisible}
      >
        <ScrollToBottomButton
          label={t(
            i18nLocale,
            'assistant:actions.scrollDown',
            'Scroll to bottom'
          )}
          iconName={IconName.ARROW_DOWNWARD}
          onClick={() => dispatchAction(scrollToBottom())}
        />
      </ScrollToBottomButtonContainer>
      <Toolbox agentType={agentType} />
      <FileDropZone
        i18nLocale={i18nLocale}
        onDropFiles={
          isInputBlocked || isEditingPlan ? undefined : documents.onDropFiles
        }
      >
        <ChatFooter
          pageName={pageName}
          pageIcon={pageIcon}
          dataScopeTooltip={dataScopeTooltip}
          placeholder={placeholder}
          input={input}
          isRequestPending={isRequestPending}
          isInputBlocked={isInputBlocked}
          isEditingPlan={isEditingPlan}
          isCancelRequestPending={isCancelRequestPending}
          onInputChange={commands.setInput}
          historyPlacement={historyPlacement}
          documents={documents}
          canSendMessage={canSendMessage}
          i18nLocale={i18nLocale}
          sendMessage={sendMessage}
          cancelMessage={commands.cancel}
          hasPendingInputFocusRequest={hasPendingInputFocusRequest}
          banners={banners}
          onDismissBanner={commands.dismissBanner}
          onConsumedInputFocusRequest={commands.requestAssistantInputFocus}
          assistantConfigInternal={
            assistantConfigInternal
              ? {
                  config: assistantConfigInternal,
                  onConfigChange: commands.setAssistantConfig,
                }
              : undefined
          }
        />
      </FileDropZone>
    </FooterAnchor>
  );
};

const FooterAnchor = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ScrollToBottomButtonContainer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: calc(100% + ${s8});
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${transitionTokens.large} ease;
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
`;

const ScrollToBottomButton = styled(IconButton)`
  width: 34px;
  height: 34px;
  background-color: ${colors.white950};
  border: 1px solid ${colors.grey300};
  &:hover {
    background-color: ${colors.grey100};
  }
`;
